/**
 * Zustand store для управления чатом
 */

import { create } from 'zustand';
import { Message } from '@/types/message.types';
import { generateUUID } from '@/utils/uuid';
import { getStorageItem, setStorageItem } from '@/utils/storage';
import { STORAGE_KEYS, LIMITS } from '@/constants/defaults';

let saveTimer: ReturnType<typeof setTimeout> | null = null;
const SAVE_DEBOUNCE_MS = 300;

/**
 * Module-level backup for conversationId.
 * Survives React tree destroy/recreate cycles (e.g. widget preview toggle)
 * while the UMD script stays loaded.
 */
let _moduleConversationId: string | null = null;

/**
 * Состояние чата
 */
interface ChatState {
  /** ID пользователя (генерируется один раз) */
  userId: string;
  
  /** ID текущей беседы */
  conversationId: string | null;
  
  /** Массив сообщений */
  messages: Message[];
  
  /** Идет ли сейчас печать AI */
  isTyping: boolean;
  
  /** Текущий task_id (для остановки генерации) */
  currentTaskId: string | null;
  
  /** Приветственное сообщение из Dify */
  welcomeMessage: string;
  
  /** Начальные быстрые вопросы */
  initialQuestions: string[];
}

/**
 * Действия (actions) для чата
 */
interface ChatActions {
  /** Добавить сообщение */
  addMessage: (message: Message) => void;
  
  /** Обновить сообщение */
  updateMessage: (id: string, updates: Partial<Message>) => void;
  
  /** Удалить сообщение */
  removeMessage: (id: string) => void;
  
  /** Очистить все сообщения */
  clearMessages: () => void;
  
  /** Установить conversation ID */
  setConversationId: (id: string) => void;
  
  /** Надёжно получить conversationId (store → module var → localStorage) */
  getConversationId: () => string | null;
  
  /** Начать новую беседу */
  startNewConversation: () => void;
  
  /** Установить состояние печати */
  setIsTyping: (isTyping: boolean) => void;
  
  /** Установить task ID */
  setCurrentTaskId: (taskId: string | null) => void;
  
  /** Установить welcome message */
  setWelcomeMessage: (message: string) => void;
  
  /** Установить initial questions */
  setInitialQuestions: (questions: string[]) => void;
  
  /** Загрузить данные из localStorage */
  loadFromStorage: () => void;
  
  /** Сохранить в localStorage */
  saveToStorage: () => void;
}

/**
 * Тип всего store
 */
type ChatStore = ChatState & ChatActions;

/**
 * Начальное состояние
 */
const initialState: ChatState = {
  userId: getStorageItem(STORAGE_KEYS.USER_ID, generateUUID()),
  conversationId: getStorageItem(STORAGE_KEYS.CONVERSATION_ID, null),
  messages: [],
  isTyping: false,
  currentTaskId: null,
  welcomeMessage: '',
  initialQuestions: [],
};

/**
 * Zustand store для чата
 */
export const useChatStore = create<ChatStore>((set, get) => ({
  ...initialState,

  addMessage: (message) => {
    set((state) => {
      const newMessages = [...state.messages, message];
      
      // Ограничиваем количество сообщений в памяти
      const trimmedMessages =
        newMessages.length > LIMITS.CACHED_MESSAGES_COUNT
          ? newMessages.slice(-LIMITS.CACHED_MESSAGES_COUNT)
          : newMessages;

      return { messages: trimmedMessages };
    });

    // Сохраняем в localStorage
    get().saveToStorage();
  },

  updateMessage: (id, updates) => {
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === id ? { ...msg, ...updates } : msg
      ),
    }));

    get().saveToStorage();
  },

  removeMessage: (id) => {
    set((state) => ({
      messages: state.messages.filter((msg) => msg.id !== id),
    }));

    get().saveToStorage();
  },

  clearMessages: () => {
    set({ messages: [] });
    get().saveToStorage();
  },

  setConversationId: (id) => {
    set({ conversationId: id });
    _moduleConversationId = id;
    setStorageItem(STORAGE_KEYS.CONVERSATION_ID, id);
  },

  startNewConversation: () => {
    _moduleConversationId = null;
    set({
      conversationId: null,
      messages: [],
      currentTaskId: null,
    });
    
    setStorageItem(STORAGE_KEYS.CONVERSATION_ID, null);
    setStorageItem(STORAGE_KEYS.MESSAGES_HISTORY, []);
  },

  setIsTyping: (isTyping) => {
    set({ isTyping });
  },

  setCurrentTaskId: (taskId) => {
    set({ currentTaskId: taskId });
  },

  setWelcomeMessage: (message) => {
    set({ welcomeMessage: message });
  },

  setInitialQuestions: (questions) => {
    set({ initialQuestions: questions });
  },

  loadFromStorage: () => {
    const userId = getStorageItem(STORAGE_KEYS.USER_ID, generateUUID());
    const conversationId =
      _moduleConversationId
      || getStorageItem(STORAGE_KEYS.CONVERSATION_ID, null);
    const messages = getStorageItem<Message[]>(STORAGE_KEYS.MESSAGES_HISTORY, []);

    setStorageItem(STORAGE_KEYS.USER_ID, userId);
    if (conversationId) _moduleConversationId = conversationId;

    set({
      userId,
      conversationId,
      messages,
    });
  },

  getConversationId: () => {
    return get().conversationId
      || _moduleConversationId
      || getStorageItem(STORAGE_KEYS.CONVERSATION_ID, null);
  },

  saveToStorage: () => {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      const { messages } = get();
      setStorageItem(STORAGE_KEYS.MESSAGES_HISTORY, messages);
    }, SAVE_DEBOUNCE_MS);
  },
}));

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (saveTimer) {
      clearTimeout(saveTimer);
      const { messages } = useChatStore.getState();
      setStorageItem(STORAGE_KEYS.MESSAGES_HISTORY, messages);
    }
  });
}

